import {
  MONETIZATION_LABEL,
  MONETIZATION_TONE,
  NGUON_TIEN_LABEL,
  REVENUE_SOURCE_LABEL,
} from '@qlk/shared';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { kyNgoaiMocDuLieu, kyTuMa, laMaKy, MA_KY_MAC_DINH } from '../../../lib/kyTrang.js';
import { coQuyen, useToi } from '../../../lib/auth.js';
import {
  Avatar,
  EmptyState,
  ChonKy,
  MenuThaoTac,
  Money,
  Nut,
  PageHeader,
  StatStrip,
  StatusBadge,
  Tabs,
  dinhDangTien,
  gonSo,
  type OChiSo,
} from '../../../ui/index.js';
import { useMocSoLieu } from '../../analytics/api.js';
import { useHieuSuatDoanhThu } from '../../analytics/hieuSuatApi.js';
import { useDoanhThuKenh } from '../../revenue/api.js';
import { useDoiNguKenh } from '../../team/api.js';
import { useCapNhatKenh, useKenh, useKhoiPhucKenh, useLuuTruKenh } from '../api.js';
import { HopLuuTruKenh } from '../components/HopLuuTruKenh.js';
import { HopXoaKenh } from '../components/HopXoaKenh.js';
import {
  NHOM_TAB_KENH,
  PHAN_TICH_CU_SANG_MUC,
  TAB_CU_SANG_NHOM,
  nhomTabTheoQuyen,
  type NhomTabKenh,
} from '../tabRegistry.js';

/**
 * Trang chi tiết kênh — bước 2/3 của nâng cấp giao diện.
 *
 * **Chín tab còn năm.** Mỗi module khai nhóm của mình trong `channelTabs.tsx`
 * (xem `tabRegistry`), trang chỉ dựng khung: đầu trang, dải chỉ số, hàng tab,
 * rồi xếp các mục của nhóm đang mở.
 *
 * Hai thứ nay nằm **trên URL**:
 *
 * - `?tab=` — tab đang mở. Trước đây là state trong React nên gửi link cho
 *   người khác là họ luôn mở ra tab đầu tiên.
 * - `?ky=` — kỳ của cả trang, **kể cả dải chỉ số và mọi biểu đồ**: `28d`
 *   (mặc định), `90d` hoặc `YYYY-MM`. Xem `lib/kyTrang.ts`.
 *
 * ## Bước 3 (22/10/2026): một ô chọn kỳ cho cả trang
 *
 * Bản trước có **hai** ô chọn: `?thang=` ở đầu trang cho ba ô tiền, và một bộ
 * chọn ngày riêng nằm trong khối "Số liệu phân tích về kênh" (mặc định 28
 * ngày). Hai ô nghĩa là hai kỳ cùng hiện trên một màn: dải nói tháng 9, biểu đồ
 * ngay dưới nói 28 ngày qua. Nay một ô, và mục nào cũng nhận `ky` của trang.
 *
 * Khối theo **tháng** (dòng tiền sao kê, Network & hợp đồng, đội ngũ & chia sẻ,
 * chi phí) vẫn tính theo tháng vì kỳ tiền của công ty là tháng: chọn kỳ lăn thì
 * chúng chạy theo **tháng chứa ngày cuối kỳ** và trang treo một dải nói đúng
 * điều đó ngay trên chúng.
 *
 * Link cũ vẫn mở được: `?tab=<id tab cũ>` đổi sang nhóm mới và nhảy tới neo
 * `#muc-<id cũ>`; `?thang=YYYY-MM` thành `?ky=YYYY-MM`; `?phanTich=<tab con>`
 * đổi sang tab chứa mục đó (xem `PHAN_TICH_CU_SANG_MUC`).
 */

function laNhom(x: string): x is NhomTabKenh {
  return (NHOM_TAB_KENH as readonly string[]).includes(x);
}

export function TrangChiTietKenh() {
  const { id = '' } = useParams();
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'channels', 'edit');
  const coAdmin = coQuyen(toi.data?.permissions, 'channels', 'admin');
  const coXemTeam = coQuyen(toi.data?.permissions, 'team', 'view');
  const coXemPhanTich = coQuyen(toi.data?.permissions, 'analytics', 'view');
  const dieuHuong = useNavigate();

  const [thamSo, datThamSo] = useSearchParams();
  const nhomTab = useMemo(() => nhomTabTheoQuyen(toi.data?.permissions), [toi.data?.permissions]);

  const thoTab = thamSo.get('tab') ?? '';
  const maKy = thamSo.get('ky') ?? '';

  /*
   * Mốc dữ liệu (ngày gần nhất **có số liệu**) là chỗ neo của kỳ lăn: YouTube
   * Analytics trễ 1–3 ngày nên neo vào hôm nay thì "28 ngày qua" của app ngắn
   * hơn 28 ngày thật của Studio.
   */
  /* Mốc của **đúng kênh này**: ô chọn kỳ ở đây không được bày năm của kênh
     khác. Kênh mở từ 2026 thì nhóm "Theo năm" chỉ có 2026. */
  const mocTruyVan = useMocSoLieu(coXemPhanTich, id === '' ? undefined : id);
  const cacNam = mocTruyVan.data?.cacNam;
  const moc = useMemo(
    () => ({
      ngayCuoi: mocTruyVan.data?.ngayCuoiCoSoLieu ?? '',
      ngayDau: mocTruyVan.data?.ngaySomNhatCoSoLieu ?? '',
      cacNam: cacNam ?? [],
    }),
    [mocTruyVan.data?.ngayCuoiCoSoLieu, mocTruyVan.data?.ngaySomNhatCoSoLieu, cacNam],
  );
  const ky = useMemo(() => kyTuMa(maKy === '' ? MA_KY_MAC_DINH : maKy, moc), [maKy, moc]);
  /** Kỳ tiền `YYYY-MM` của kỳ này — khối nào còn chỉ đọc được theo tháng thì dùng. */
  const thang = ky.thang;

  /*
   * Chuyển hướng link cũ: `?tab=dong-bo` → `?tab=cai-dat#muc-dong-bo`. Làm
   * trong effect (không phải khi render) vì nó **ghi** vào URL.
   */
  useEffect(() => {
    if (thoTab === '' || laNhom(thoTab)) return;

    const nhomMoi = TAB_CU_SANG_NHOM[thoTab];
    if (nhomMoi === undefined) return;

    const tiep = new URLSearchParams(thamSo);
    tiep.set('tab', nhomMoi);
    datThamSo(tiep, { replace: true });
    /* Neo đặt sau khi React vẽ xong nhóm mới. */
    requestAnimationFrame(() => {
      document.getElementById(`muc-${thoTab}`)?.scrollIntoView({ block: 'start' });
    });
  }, [thoTab, thamSo, datThamSo]);

  /* `?thang=YYYY-MM` của bản trước → `?ky=YYYY-MM`, và mã kỳ lạ → mặc định. */
  const thoThang = thamSo.get('thang') ?? '';
  useEffect(() => {
    if (thoThang === '' && (maKy === '' || laMaKy(maKy))) return;

    const tiep = new URLSearchParams(thamSo);
    tiep.delete('thang');
    /* `?ky=` hợp lệ thì nó thắng: link vừa có `?thang=` cũ vừa có `?ky=` mới
       là link đã đi qua bản mới một lần. */
    tiep.set('ky', laMaKy(maKy) ? maKy : laMaKy(thoThang) ? thoThang : MA_KY_MAC_DINH);
    datThamSo(tiep, { replace: true });
  }, [thoThang, maKy, thamSo, datThamSo]);

  /* `?phanTich=<tab con cũ>` → tab chứa mục đó, kèm neo tới đúng mục. */
  const thoPhanTich = thamSo.get('phanTich') ?? '';
  useEffect(() => {
    if (thoPhanTich === '') return;

    const dich = PHAN_TICH_CU_SANG_MUC[thoPhanTich];
    const tiep = new URLSearchParams(thamSo);
    tiep.delete('phanTich');
    if (dich) tiep.set('tab', dich.nhom);
    datThamSo(tiep, { replace: true });

    if (!dich) return;
    requestAnimationFrame(() => {
      document.getElementById(`muc-${dich.muc}`)?.scrollIntoView({ block: 'start' });
    });
  }, [thoPhanTich, thamSo, datThamSo]);

  const nhomDangMo = laNhom(thoTab)
    ? (nhomTab.find((n) => n.nhom === thoTab) ?? nhomTab[0])
    : nhomTab[0];

  const datTab = (giaTri: string) => {
    const tiep = new URLSearchParams(thamSo);
    tiep.set('tab', giaTri);
    datThamSo(tiep);
  };

  const datKy = (giaTri: string) => {
    const tiep = new URLSearchParams(thamSo);
    tiep.set('ky', giaTri);
    tiep.delete('thang');
    datThamSo(tiep);
  };

  const kenh = useKenh(id);
  /*
   * Lợi nhuận và chi phí của **đúng kỳ đang chọn** lấy từ module 10: kỳ tháng
   * gửi `?period=`, kỳ khoảng gửi `?from=&to=` (xem `team/api.ts`). Thiếu
   * quyền nhân sự thì ô lợi nhuận ghi lý do thay vì số.
   */
  const doiNgu = useDoiNguKenh(id, ky);
  /*
   * Doanh thu gộp và thực nhận của **đúng kỳ đang chọn**, lấy từ module 08.
   *
   * Không dùng `k.revenueSummary` cho dải chỉ số nữa: enricher đó luôn chạy với
   * cửa sổ 28 ngày cố định (`getChannel` truyền `period: '28d'`), nên ô chọn
   * tháng không đổi được số của nó.
   *
   * Người không có `revenue:view` thì không gọi: ô tiền để trống kèm lý do, chứ
   * không bắn một request chắc chắn 403 mỗi lần mở trang.
   */
  const coXemTien = coQuyen(toi.data?.permissions, 'revenue', 'view');
  /* Dòng doanh thu của **tháng**: chỉ dùng cho kỳ tháng, nên kỳ khoảng không gọi. */
  const doanhThu = useDoanhThuKenh(thang, coXemTien && !ky.theoKhoang);
  const dongTien = useMemo(
    () => (doanhThu.data ?? []).find((d) => d.channelId === id) ?? null,
    [doanhThu.data, id],
  );

  /*
   * Kỳ **lăn** (28/90 ngày) không có dòng doanh thu tháng nào để đọc: bảng
   * `revenue_monthly` khoá theo `YYYY-MM`. Gộp và thực nhận của khoảng ngày lấy
   * từ module 07 (`GET /analytics/kenh/:id/doanh-thu?from&to`), mà endpoint đó
   * tính thực nhận bằng **đúng hàm chung** `revenue.layCaiDatThucNhan` trên
   * doanh thu từng ngày — không có phép tính thứ hai cho tiền.
   */
  /*
   * Chỉ gọi khi **không** có chuỗi của module 10 cho kỳ này: người có quyền
   * nhân sự và thấy hết tiền đã nhận cả gộp, thực nhận và lợi nhuận trong một
   * lượt `doi-ngu`, bắn thêm một request nữa chỉ để lấy lại hai con số đó là
   * hai đường tính cho cùng một ô.
   */
  const coChuoiModule10 = coXemTeam && doiNgu.data !== undefined && !doiNgu.data.chiaKy.daChe;
  const batDoanhThuKhoang =
    ky.theoKhoang && ky.sanSang && coXemTien && coXemPhanTich && !coChuoiModule10;
  const doanhThuKhoang = useHieuSuatDoanhThu(
    id,
    { from: ky.from, to: ky.to, ma: ky.ma },
    batDoanhThuKhoang,
  );

  const capNhat = useCapNhatKenh(id);
  const luuTru = useLuuTruKenh(id);
  const khoiPhuc = useKhoiPhucKenh(id);
  const [moHopLuuTru, setMoHopLuuTru] = useState(false);
  const [moHopXoa, setMoHopXoa] = useState(false);

  if (kenh.isPending) {
    return <p className="text-co-ban text-chu-phu">Đang tải kênh…</p>;
  }

  if (kenh.error) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState
          loi
          title="Không mở được kênh"
          description={kenh.error.message}
          action={
            <Link to="/kenh" className="text-co-ban font-semibold text-nhan">
              Về danh sách kênh
            </Link>
          }
        />
      </div>
    );
  }

  const k = kenh.data;
  const chiaKy = coXemTeam && doiNgu.data && !doiNgu.data.chiaKy.daChe ? doiNgu.data.chiaKy : null;
  const nhanKy = ky.nhan.toLowerCase();
  /** Kỳ không trùng trọn một tháng: số tiền là tạm tính (xem `lib/kyTrang.ts`). */
  const theoKhoang = ky.theoKhoang;

  /*
   * Dải chỉ số nói về **một** kỳ: đúng kỳ ở ô chọn đầu trang.
   *
   * Hai đường cho ba ô tiền, theo loại kỳ — **một** đường mỗi lượt:
   *
   * - kỳ **tháng**: có quyền nhân sự thì lấy cả chuỗi của module 10 (gộp →
   *   thực nhận → lợi nhuận, cùng một lượt tính), không thì lấy gộp và thực
   *   nhận của module 08;
   * - kỳ **khoảng** (28/90/365 ngày, năm, toàn bộ thời gian, khoảng tự chọn):
   *   thiếu chuỗi module 10 thì gộp và thực nhận lấy của khoảng ngày từ module
   *   07 (`GET /analytics/kenh/:id/doanh-thu?from&to`).
   *
   * Mọi đường đều đi qua `layThucNhanKyKenh` / `layCaiDatThucNhan`, nên hai kỳ
   * trùng ngày cho ra cùng một con số.
   */
  const thucNhanKhoang = doanhThuKhoang.data?.thucNhan ?? null;
  const gop =
    chiaKy?.thucNhanChiTiet?.doanhThu ??
    (theoKhoang ? (thucNhanKhoang?.doanhThu ?? null) : (dongTien?.grossUsed ?? null));
  const thucNhan = chiaKy
    ? chiaKy.ketQua.thucNhan
    : theoKhoang
      ? (thucNhanKhoang?.thucNhan ?? null)
      : (dongTien?.thucNhan ?? null);
  const phuThieuTien = !coXemTien
    ? 'cần quyền doanh thu'
    : theoKhoang && !coXemPhanTich && !coXemTeam
      ? 'cần quyền phân tích'
      : 'chưa có số của kỳ này';

  /*
   * Chip `tạm tính` — **chỉ** ở ô "Lợi nhuận kênh", và chỉ khi kỳ không trùng
   * trọn một tháng. Nó nói đúng một câu: con số có thật và tính bằng đúng
   * chuỗi của kỳ tháng, nhưng **chứng từ** (sao kê Network, biên bản chia,
   * đối soát nhân sự) chỉ có theo tháng.
   *
   * Một chip chứ không ba: hai ô kia đã tự khai nguồn ("nguồn: youtube api
   * (ước tính)"), còn ba chip giống hệt nhau trong một dải bốn ô là tiếng ồn —
   * đọc tới cái thứ ba thì không ai còn đọc nữa.
   */
  const chipTamTinh = theoKhoang ? (
    <StatusBadge tone="warn" cham={false}>
      <span title="Số đối soát chính thức tính theo tháng.">tạm tính</span>
    </StatusBadge>
  ) : null;

  const chiPhiKy = chiaKy?.ketQua.chiPhiVanHanh ?? 0;
  const phuLoiNhuan = [
    chiPhiKy > 0 ? `trừ chi phí ${dinhDangTien(chiPhiKy)}` : null,
    chiaKy?.ketQua.kenhLo === true ? 'kênh lỗ — chia theo thực nhận' : null,
  ]
    .filter((x): x is string => x !== null)
    .join(' · ');

  /*
   * Ba ô tiền (gộp, thực nhận, lợi nhuận) chỉ dành cho người có
   * `revenue:view` — rà soát bảo mật 31/10/2026, mục **S5**.
   *
   * Bỏ hến ba ô, **không** hiện `—` kèm lý do như bản trước: một ô mang
   * nhãn "Doanh thu gộp" vẫn nói ra ở đây có một con số tiền, và dòng phụ
   * "cần quyền nhân sự" nói luôn là phải xin quyền nào. Dải chỉ số của họ
   * còn ô Subscribers và các ô không phải tiền.
   */
  const o: OChiSo[] = [
    ...(!coXemTien
      ? []
      : ([
          {
            nhan: `Doanh thu gộp · ${nhanKy}`,
            giaTri: <Money value={gop} />,
            phu:
              gop === null
                ? phuThieuTien
                : dongTien && !theoKhoang
                  ? `nguồn: ${REVENUE_SOURCE_LABEL[dongTien.grossSource].toLowerCase()}`
                  : 'nguồn: youtube api (ước tính)',
          },
          {
            nhan: `Thực nhận · ${nhanKy}`,
            giaTri: <Money value={thucNhan} />,
            ...(thucNhan === null ? { phu: phuThieuTien } : {}),
          },
          {
            /*
             * Lợi nhuận = thực nhận **trừ chi phí vận hành của kỳ**, và nay có số ở
             * **mọi** kỳ: bản trước để `—` cho kỳ lăn vì chi phí và phần chia chỉ
             * tính theo tháng, nhưng một ô trống giữa ba ô có số đọc như app hỏng.
             *
             * Không có công thức thứ hai nào sinh ra: kỳ khoảng vẫn đi qua
             * `tinhChiaKyKenh` với `from`/`to`, chỉ khác ba chỗ đã ghi trong
             * `TuyChonChia.khoang` — thực nhận cộng từ số liệu từng ngày, khoản chi
             * thuê tháng phân theo số ngày, và **lỗ cộng dồn bị bỏ qua**. Vì vậy số
             * của kỳ khoảng gắn chip `tạm tính`, còn số đối soát vẫn là số của tháng.
             */
            nhan: `Lợi nhuận kênh · ${nhanKy}`,
            giaTri: chiaKy ? <Money value={chiaKy.ketQua.loiNhuan} /> : '—',
            phu: chiaKy ? (
              <>
                {phuLoiNhuan === '' ? 'chưa có chi phí nào trong kỳ' : phuLoiNhuan} {chipTamTinh}
              </>
            ) : coXemTeam ? (
              'chưa có số của kỳ này'
            ) : (
              'cần quyền nhân sự'
            ),
          },
        ] satisfies OChiSo[])),
    {
      /* Subscribers là số **tại thời điểm này**, không phải số của kỳ: ghi rõ
         "hiện tại" để nó không bị đọc như ba ô tiền bên cạnh. */
      nhan: 'Subscribers · hiện tại',
      giaTri: gonSo(k.metrics?.subscribers),
    },
  ];

  return (
    <>
      <PageHeader
        title={k.name}
        phu={`${k.handle} · ${k.niche} · ${k.language}`}
        actions={
          <>
            {k.youtubeChannelId !== null && (
              <a
                href={`https://studio.youtube.com/channel/${k.youtubeChannelId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="vung-bam inline-flex items-center justify-center rounded-o border border-vien bg-be-mat px-3 text-co-ban font-semibold text-chu hover:bg-neutral-nen"
              >
                Mở trong Studio ↗
              </a>
            )}

            {k.archivedAt === null ? (
              /* Mọi thao tác với chính cái kênh gom vào một nút ⋯: trước đây
                 bốn nút chữ xếp ngang làm đầu trang rộng hơn cả tên kênh. */
              <MenuThaoTac
                aria-label={`Thao tác với kênh ${k.name}`}
                muc={[
                  ...(coSua
                    ? [
                        {
                          nhan: 'Sửa kênh',
                          onChon: () => {
                            void dieuHuong(`/kenh/${k.id}/sua`);
                          },
                        },
                        {
                          nhan: 'Lưu trữ kênh',
                          nguyHiem: true,
                          onChon: () => {
                            setMoHopLuuTru(true);
                          },
                        },
                      ]
                    : []),
                  {
                    nhan: 'Về danh sách kênh',
                    onChon: () => {
                      void dieuHuong('/kenh');
                    },
                  },
                ]}
              />
            ) : (
              <>
                {coSua && (
                  <Nut
                    kieu="chinh"
                    dangChay={khoiPhuc.isPending}
                    onClick={() => {
                      khoiPhuc.mutate();
                    }}
                  >
                    Khôi phục kênh
                  </Nut>
                )}
                {/* Xoá vĩnh viễn chỉ dành cho admin và chỉ với kênh đã lưu trữ. */}
                {coAdmin && (
                  <Nut
                    kieu="nguy-hiem"
                    onClick={() => {
                      setMoHopXoa(true);
                    }}
                  >
                    Xoá vĩnh viễn
                  </Nut>
                )}
              </>
            )}
          </>
        }
      />

      {/* Dòng chip: trạng thái của kênh, đọc một lượt là biết kênh đang thế nào. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Avatar name={k.name} color={k.avatarColor} size={48} vuong />
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge tone={MONETIZATION_TONE[k.monetizationStatus]}>
            {MONETIZATION_LABEL[k.monetizationStatus]}
          </StatusBadge>

          {k.payment ? (
            <StatusBadge tone={k.payment.kind === 'network' ? 'info' : 'teal'} cham={false}>
              {k.payment.partnerName}
            </StatusBadge>
          ) : (
            <StatusBadge tone="neutral" cham={false}>
              {NGUON_TIEN_LABEL['chua-kiem-tien']}
            </StatusBadge>
          )}

          {k.chiaSe?.tenNhom !== null && k.chiaSe?.tenNhom !== undefined && (
            <StatusBadge tone="neutral" cham={false}>
              {k.chiaSe.tenNhom}
            </StatusBadge>
          )}

          {k.syncStatus && (
            <StatusBadge tone={k.syncStatus.connected ? 'ok' : 'neutral'}>
              {k.syncStatus.connected ? 'Đã kết nối YouTube' : 'Chưa kết nối YouTube'}
            </StatusBadge>
          )}

          {k.syncStatus?.message !== null && k.syncStatus?.message !== undefined && (
            <StatusBadge tone="warn">
              <span title={k.syncStatus.message}>Đồng bộ đang lỗi</span>
            </StatusBadge>
          )}
        </div>

        {/*
          Kỳ của cả trang: dải chỉ số, mọi biểu đồ và **mọi** mục trong tab đều
          theo ô này — kể cả bốn khối của tab Doanh thu & chia sẻ, vốn chỉ chạy
          theo tháng tới bản 23/10/2026.
        */}
        <div className="ms-auto">
          <ChonKy ky={ky} moc={moc} onChon={datKy} nhanTrai="Kỳ của cả trang" hienKhoang />
        </div>
      </div>

      <div className="mb-4">
        <StatStrip
          aria-label={`Chỉ số của kênh ${k.name} · ${ky.nhan}${ky.tamTinh ? ' (tạm tính)' : ''}`}
          o={o}
          /* Kỳ ngoài mốc dữ liệu (link cũ `?ky=2017`): API trả 0, mà `0,00 $` đọc
           ra là "tụt về không". Hiện `—`; dòng "Không có số liệu trong kỳ này"
           nằm cạnh ô chọn kỳ (`ui/ChonKy`). */
          kyRong={kyNgoaiMocDuLieu(ky, moc)}
        />
      </div>

      {k.archivedAt !== null && (
        <div className="mb-4 rounded-the border border-vien bg-warn-nen p-4 text-co-ban text-warn-chu">
          <p className="font-semibold">Kênh đã lưu trữ</p>
          <p className="mt-1">
            Kênh không hiện ở danh sách, Tổng quan hay Doanh thu kỳ mới, và job đồng bộ bỏ qua nó.
            Số liệu, doanh thu và sao kê vẫn giữ để đối soát. Khôi phục xong{' '}
            <strong>cần kết nối lại Google</strong> vì token đã bị thu hồi.
          </p>
        </div>
      )}

      {k.archivedAt === null && !k.apiTracked && (
        <div className="mb-4 flex flex-wrap items-center gap-4 rounded-the border border-vien bg-neutral-nen p-4 text-co-ban text-chu-phu">
          <div>
            <p className="font-semibold text-chu">Không theo dõi qua API</p>
            <p className="mt-1">
              Kênh không hiện ở màn hình Kết nối API, không tính vào “N/M kênh đã kết nối”, và job
              đồng bộ bỏ qua. Số liệu cũ vẫn giữ.
            </p>
          </div>
          {coSua && (
            <div className="ms-auto">
              <Nut
                kieu="chinh"
                dangChay={capNhat.isPending}
                onClick={() => {
                  capNhat.mutate({ apiTracked: true });
                }}
              >
                Bật theo dõi qua API
              </Nut>
            </div>
          )}
        </div>
      )}

      {luuTru.data && (
        <div className="mb-4 rounded-the border border-vien bg-info-nen p-4 text-co-ban text-info-chu">
          <p className="font-semibold">Đã lưu trữ kênh</p>
          <ul className="mt-1 list-inside list-disc">
            {luuTru.data.daLam.map((v) => (
              <li key={v.ten}>
                {v.ten}: {v.moTa}
              </li>
            ))}
            {luuTru.data.khongLamDuoc.map((v) => (
              <li key={v.ten} className="text-bad-chu">
                {v.ten}: {v.lyDo}
              </li>
            ))}
          </ul>
        </div>
      )}

      {moHopLuuTru && (
        <HopLuuTruKenh
          tenKenh={k.name}
          dangChay={luuTru.isPending}
          loi={luuTru.error?.message ?? null}
          onDong={() => {
            setMoHopLuuTru(false);
          }}
          onXacNhan={() => {
            luuTru.mutate(undefined, { onSuccess: () => setMoHopLuuTru(false) });
          }}
        />
      )}

      {moHopXoa && (
        <HopXoaKenh
          channelId={k.id}
          tenKenh={k.name}
          onDong={() => {
            setMoHopXoa(false);
          }}
          onXong={() => {
            setMoHopXoa(false);
            void dieuHuong('/kenh');
          }}
        />
      )}

      {nhomTab.length === 0 ? (
        <EmptyState title="Không có mục nào để xem" />
      ) : (
        <>
          <Tabs
            aria-label="Thông tin kênh"
            value={nhomDangMo?.nhom ?? ''}
            onChange={datTab}
            items={nhomTab.map((n) => ({ id: n.nhom, label: n.label }))}
          />

          <div className="mt-4">
            <Suspense fallback={<p className="text-co-ban text-chu-phu">Đang tải…</p>}>
              {/* Mục `cot: 'dai'` — dải chỉ số của tab, trọn bề ngang, trên lưới. */}
              {(nhomDangMo?.dai ?? []).length > 0 && (
                <div className="mb-4 flex flex-col gap-4">
                  {(nhomDangMo?.dai ?? []).map((t) => (
                    <t.component key={t.id} channelId={k.id} thang={thang} ky={ky} />
                  ))}
                </div>
              )}

              {nhomDangMo && nhomDangMo.phai.length > 0 ? (
                /*
                 * Nhóm hai cột (Doanh thu & chia sẻ): trái là dữ liệu vào, phải
                 * là phép tính. Trên khổ hẹp cột phải xuống dưới — đọc dữ liệu
                 * trước rồi tới kết quả vẫn đúng thứ tự.
                 */
                <div className="grid grid-cols-1 items-start gap-4 @min-[1000px]/noi-dung:grid-cols-[minmax(0,1.55fr)_minmax(320px,1fr)]">
                  <div className="flex min-w-0 flex-col gap-4">
                    {nhomDangMo.trai.map((t) => (
                      <t.component key={t.id} channelId={k.id} thang={thang} ky={ky} />
                    ))}
                  </div>
                  <div className="flex min-w-0 flex-col gap-4">
                    {nhomDangMo.phai.map((t) => (
                      <t.component key={t.id} channelId={k.id} thang={thang} ky={ky} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {(nhomDangMo?.muc ?? [])
                    .filter((t) => t.cot !== 'dai')
                    .map((t) => (
                      <t.component key={t.id} channelId={k.id} thang={thang} ky={ky} />
                    ))}
                </div>
              )}
            </Suspense>
          </div>
        </>
      )}

      {!coSua && (
        <p className="mt-3 text-phu text-chu-phu">
          Bạn đang ở chế độ chỉ xem, cần quyền sửa ở module Quản lý kênh để thay đổi dữ liệu.
        </p>
      )}
    </>
  );
}
